import { inject, Injectable } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { AnalyticsService } from '@app/core/analytics/analytics.service';
import { AppConfig } from '@app/core/app.config';
import { AnalyticsKeyEnum } from '@app/core/enums/analytics/analytics-key.enum';
import { ActionEnum } from '@app/core/enums/analytics/analytics-value.enum';
import { DocumentType } from '@app/core/enums/document/document-type.enum';
import { EntityTypeEnum } from '@app/core/enums/entity-type.enum';
import { AnalyticsEvent } from '@app/core/model/entities/analytics/analytics-event';
import { Document } from '@app/core/model/entities/document/document';
import { EntityInput } from '@app/core/model/entities/entity';
import { Equipment } from '@app/core/model/entities/equipments/equipment';
import { Project } from '@app/core/model/entities/project/project';
import { Work } from '@app/core/model/entities/works/work';
import {
  AssetWorkCreateModalComponent
} from '@app/features/main/views/assets/asset-sheet/works/modals/asset-work-create-modal/asset-work-create-modal.component';
import {
  EquipmentWorkCreateModalComponent
} from '@app/features/main/views/equipments/equipment-sheet/works/modals/equipment-work-create-modal/equipment-work-create-modal.component';
import {
  MeetingWorkCreateModalComponent
} from '@app/features/main/views/meetings/meeting-sheet/works/modals/meeting-work-create-modal.component';
import { DocumentsService } from '@app/features/main/views/organization-documents/documents.service';
import { DocumentModalService } from '@app/features/main/views/organization-documents/modals/document-modal.service';
import {
  ProjectWorkCreateModalComponent
} from '@app/features/main/views/projects/project-sheet/works/modals/project-work-create-modal/project-work-create-modal.component';
import {
  WorkCreateModalComponent
} from '@app/features/main/views/works/works-inventory/modals/work-create-modal/work-create-modal.component';
import {
  WorkDuplicateModalComponent
} from '@app/features/main/views/works/works-inventory/modals/work-duplicate-modal/work-duplicate-modal.component';
import {
  WorkUpdateProjectsModalComponent
} from '@app/features/main/views/works/works-inventory/modals/work-update-modal/work-update-projects-modal.component';
import { WorksService } from '@app/features/main/views/works/works.service';
import { SpinnerService } from '@app/shared/popup/spinner.service';
import { TranslateService } from '@ngx-translate/core';
import { PopupManager, PopupSize } from '@services/managers/popup.manager';
import { EMPTY, finalize, Observable, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';

@Injectable()
export class WorkModalService {

  private readonly popupManager = inject(PopupManager);
  private readonly appConfig = inject(AppConfig);
  private readonly worksService = inject(WorksService);
  private readonly analyticsService = inject(AnalyticsService);
  private readonly translate = inject(TranslateService);
  private readonly documentModalService = inject(DocumentModalService);
  private readonly spinnerService = inject(SpinnerService);
  private readonly documentsService = inject(DocumentsService);

  /**
   * Display a dialog for the user to fill data then create a new Work with the data.
   * @param entityInput Optional input to link an entity to the work. Input has two entries, entity for the entity data and
   * entityType for the type of entity.
   * @return Created Work.
   */
  public openCreateWorkDialog(entityInput?: EntityInput): Observable<Work> {
    let dialogRef: MatDialogRef<WorkCreateModalComponent>;
    switch (entityInput?.entityType) {
      case EntityTypeEnum.ASSET:
        dialogRef = this.popupManager.showGenericPopup(
          AssetWorkCreateModalComponent,
          PopupSize.MEDIUM,
          {asset: entityInput.entity}
        );
        break;
      case EntityTypeEnum.EQUIPMENT:
        dialogRef = this.popupManager.showGenericPopup(
          EquipmentWorkCreateModalComponent,
          PopupSize.MEDIUM,
          {equipment: entityInput.entity}
        );
        break;
      case EntityTypeEnum.PROJECT:
        dialogRef = this.popupManager.showGenericPopup(
          ProjectWorkCreateModalComponent,
          PopupSize.MEDIUM,
          {project: entityInput.entity}
        );
        break;
      case EntityTypeEnum.MEETING:
        dialogRef = this.popupManager.showGenericPopup(
          MeetingWorkCreateModalComponent,
          PopupSize.MEDIUM,
          {meeting: entityInput.entity}
        );
        break;
      default:
        dialogRef = this.popupManager.showGenericPopup(WorkCreateModalComponent, PopupSize.MEDIUM, {});
    }

    return dialogRef.afterClosed()
      .pipe(
        switchMap(dialogResponse => {
          const event = new AnalyticsEvent(ActionEnum.CREATE, EntityTypeEnum.WORK);

          if (dialogResponse === 'yes') {
            event.addProperties({[AnalyticsKeyEnum.DIALOG_ACTION]: ActionEnum.SAVE});
            this.analyticsService.trackEvent(event);
            this.spinnerService.showSpinner();
            return this.worksService.createWork(dialogRef.componentInstance.getGeneratedObject())
              .pipe(
                // Add equipment main picture to work if it exists and work is created from an equipment
                switchMap(work => {
                  if (entityInput?.entityType === EntityTypeEnum.EQUIPMENT) {
                    // Find equipment's main picture
                    const mainPicture = entityInput.entity.properties?.mainPictureDocumentId;
                    if (mainPicture) {
                      // Link the existing document to newly created work
                      return this.documentsService.linkDocumentToEntity(work.id, EntityTypeEnum.WORK, mainPicture)
                        // Set newly created document as the main picture for the work
                        .pipe(
                          switchMap((document) =>
                            this.worksService.updateWork(
                              work,
                              {properties: {mainPictureDocumentId: document.id}}
                            )
                          )
                        );
                    }
                  }
                  return of(work);
                }),
                finalize(() => this.spinnerService.hideSpinner())
              );
          } else {
            event.addProperties({[AnalyticsKeyEnum.DIALOG_ACTION]: ActionEnum.CANCEL});
            this.analyticsService.trackEvent(event);
            return EMPTY;
          }
        })
      );
  }

  /**
   * Display a dialog for the user to select an Asset and Spaces then copy a list of existing Works to the selected Asset.
   * @param works Works to duplicate.
   * @param project Project to whom link the Works to duplicate
   * @param equipment Equipment to whom link the Works to duplicate
   * @return Boolean of true if all works have been successfully created, false otherwise.
   */
  public openDuplicateWorksDialog(works: Work[], project?: Project, equipment?: Equipment): Observable<Work[]> {
    const dialogRef = this.popupManager.showGenericPopup(
      WorkDuplicateModalComponent,
      PopupSize.MEDIUM,
      {works, project, equipment}
    );
    return dialogRef.afterClosed()
      .pipe(
        switchMap(dialogResponse => {
          const event = new AnalyticsEvent(ActionEnum.DUPLICATE, EntityTypeEnum.WORK);

          if (dialogResponse === 'yes') {
            event.addProperties({
              [AnalyticsKeyEnum.ENTITY_ID]: works.map(work => work.id).toString(),
              [AnalyticsKeyEnum.DIALOG_ACTION]: ActionEnum.SAVE
            });
            const {assetId, spaceIds, equipmentIds, projectIds} = dialogRef.componentInstance.getGeneratedObject();
            this.analyticsService.trackEvent(event);
            this.spinnerService.showSpinner();
            return this.worksService.duplicateWorks(works, assetId, spaceIds, equipmentIds, projectIds)
              .pipe(finalize(() => this.spinnerService.hideSpinner()));
          } else {
            event.addProperties({[AnalyticsKeyEnum.DIALOG_ACTION]: ActionEnum.CANCEL});
            this.analyticsService.trackEvent(event);
            return EMPTY;
          }
        })
      );
  }

  /**
   * Display a dialog for the user to select a Project to add as a related project of the selected Works.
   * @param works Works to update.
   * @return An array of the updated works
   */
  public openAddProjectsToWorksDialog(works: Work[]): Observable<Work[]> {
    const dialogRef = this.popupManager.showGenericPopup(
      WorkUpdateProjectsModalComponent,
      PopupSize.MEDIUM,
      {works}
    );

    return dialogRef.afterClosed()
      .pipe(
        switchMap(dialogResponse => {
          const event = new AnalyticsEvent(ActionEnum.UPDATE, EntityTypeEnum.WORK);
          const workIds = works.map(work => work.id);

          if (dialogResponse === 'yes') {
            event.addProperties({
              [AnalyticsKeyEnum.ENTITY_ID]: workIds.toString(),
              [AnalyticsKeyEnum.DIALOG_ACTION]: ActionEnum.UPDATE
            });
            const projectId = dialogRef.componentInstance.getGeneratedObject();
            this.analyticsService.trackEvent(event);
            this.spinnerService.showSpinner();
            return this.worksService.addProjectToWorks(workIds, [projectId])
              .pipe(finalize(() => this.spinnerService.hideSpinner()));
          } else {
            event.addProperties({[AnalyticsKeyEnum.DIALOG_ACTION]: ActionEnum.CANCEL});
            this.analyticsService.trackEvent(event);
            return EMPTY;
          }
        })
      );
  }

  /**
   * Display a dialog asking the user to confirm Works deletion, then delete the Works if confirmed.
   * @param works Works to delete.
   * @return True if the Works have been successfully deleted, false if the user cancelled the operation.
   * @throws Any encountered error.
   */
  public openDeleteWorksDialog(works: Work[]): Observable<boolean> {
    const dialogRef = this.popupManager.showOkCancelPopup({
      dialogTitle: this.translate.instant(works.length === 1 ? 'TITLE.DELETE_WORK' : 'TITLE.DELETE_WORKS'),
      dialogMessage: this.translate.instant(works.length === 1 ?
        'LABEL.DELETE_WORK_AND_DOCUMENTS'
        : 'LABEL.DELETE_WORKS_AND_DOCUMENTS'),
      type: 'warning',
      okText: this.translate.instant('LABEL.DELETE')
    });
    return dialogRef.afterClosed()
      .pipe(
        switchMap(dialogResponse => {
          const event = new AnalyticsEvent(ActionEnum.DELETE, EntityTypeEnum.WORK);

          if (dialogResponse === 'yes') {
            event.addProperties({
              [AnalyticsKeyEnum.ENTITY_ID]: works.map(work => work.id).toString(),
              [AnalyticsKeyEnum.DIALOG_ACTION]: ActionEnum.DELETE
            });
            this.analyticsService.trackEvent(event);
            this.spinnerService.showSpinner();
            return this.worksService.deleteWorks(works)
              .pipe(finalize(() => this.spinnerService.hideSpinner()));
          } else {
            event.addProperties({[AnalyticsKeyEnum.DIALOG_ACTION]: ActionEnum.CANCEL});
            this.analyticsService.trackEvent(event);
            return of(false);
          }
        })
      );
  }

  /**
   * Display a dialog for the User to upload files and create Documents related to a Work.
   * @param workId ID of the Work to link Documents to.
   * @return Created Documents.
   * @throws Any encountered errors.
   */
  public openUploadWorkDocumentsDialog(workId: string): Observable<Document[]> {
    return this.documentModalService.openUploadEntityDocumentsDialog(
      workId,
      EntityTypeEnum.WORK,
      DocumentType.DOCUMENT
    );
  }

  /**
   * Display a dialog asking the User to confirm a Work's Document's deletion.
   * @param document Document to delete.
   * @return True if the Document was successfully deleted of false if the operation was canceled by the User.
   * @throws Any encountered errors.
   */
  public openDeleteWorkDocumentDialog(document: Document): Observable<boolean> {
    return this.documentModalService.openDeleteEntityDocumentDialog(document);
  }


  /**
   * Display a dialog for the User to upload pictures of a Work.
   * @param workId ID of the Work to link Documents to.
   * @return Created Documents.
   * @throws Any encountered errors.
   */
  public openUploadWorkPicturesDialog(workId: string): Observable<Document[]> {
    return this.documentModalService.openUploadEntityDocumentsDialog(
      workId,
      EntityTypeEnum.WORK,
      DocumentType.PICTURE,
      {
        allowedContentTypes: ['image/jpeg', 'image/png'],
        maxFileSize: this.appConfig.MAX_IMAGE_SIZE,
        title: 'TITLE.ADD_PICTURE'
      }
    );
  }

  /**
   * Display a dialog asking the User to confirm a Work's picture's deletion.
   * @param imageDocument Document representing the image to delete.
   * @return True if the image's Document was successfully deleted of false if the operation was canceled by the User.
   * @throws Any encountered errors.
   */
  public openDeleteWorkPictureDialog(imageDocument: Document): Observable<boolean> {
    return this.documentModalService.openDeleteEntityDocumentDialog(imageDocument, {
      dialogTitle: this.translate.instant('TITLE.DELETE_PICTURE'),
      dialogMessage: this.translate.instant('MESSAGE.DELETE_PICTURE'),
      okText: this.translate.instant('BUTTON.DELETE'),
      cancelText: this.translate.instant('BUTTON.CANCEL'),
      type: 'warning'
    });
  }
}
